import { useId } from "react";
import { cn } from "../lib/utils";
import { aideCouleurs, aideLargeur, aideNom } from "./cadres-textes";
import { CARTE } from "./cadres-ui";
import {
  type ApercuReponse,
  COULEURS_CADRE_MAX,
  COULEURS_CADRE_MIN,
  ECART_MAX_MM,
  ECART_MIN_MM,
  ECART_PAS_MM,
  EPAISSEUR_MAX_MM,
  EPAISSEUR_MIN_MM,
  EPAISSEUR_PAS_MM,
  type Fenetre,
  LARGEUR_CADRE_MAX_MM,
  LARGEUR_CADRE_MIN_MM,
  NOM_MOTIF,
  PASSE_PARTOUT_MM,
  type ReglagesCadre,
} from "./contract";
import { Bascule, CHAMP, Ligne, type Option } from "./silhouettes-reglages";
import { nombreFr } from "./silhouettes-textes";

/**
 * La carte Reglages du module Cadres (#167 ; maquette `Cadres.dc.html`) : nom, largeur (la
 * hauteur des plaques ecrite a cote, calculee), fenetre, couleurs, epaisseur des plaques, ecart
 * entre elles (un curseur) et eclairage LED (une case). Les controles sont ceux des silhouettes
 * (`Ligne`, `Bascule`, `CHAMP`), repris et non recopies.
 *
 * La saisie garde ce qui est tape tel quel (un nom invalide, une largeur vide) : `lireSaisie`
 * dit ce qu'elle donne, et rien ne part tant que la largeur est hors bornes. Couleurs et
 * epaisseur sont des listes, l'ecart un curseur et la LED une case : ils ne peuvent pas etre
 * faux.
 */

export type SaisieCadre = {
  nom: string;
  /** Le texte du champ, pas encore un nombre. */
  largeur: string;
  fenetre: Fenetre;
  couleurs: ReglagesCadre["couleurs"];
  epaisseur: number;
  /** L'ecart entre deux plaques, en millimetres. */
  ecart: number;
  /** Ruban LED derriere le fond : le fond s'imprime alors a 0,6 mm. */
  led: boolean;
};

/** La saisie de depart : les reglages par defaut du backend, et un nom vide. */
export function saisieDepuis(reglages: ReglagesCadre): SaisieCadre {
  return {
    // Vide jusqu'a la premiere image : le champ affiche « pris du nom de l'image ».
    nom: "",
    largeur: String(reglages.largeur_mm),
    fenetre: reglages.fenetre,
    couleurs: reglages.couleurs,
    epaisseur: reglages.epaisseur_mm,
    ecart: reglages.ecart_mm,
    led: reglages.led,
  };
}

export type LectureCadre = {
  reglages: ReglagesCadre;
  nomInvalide: boolean;
  largeurInvalide: boolean;
};

/** Les reglages que la saisie donne ; une largeur hors bornes part a 0, que le contrat refuse. */
export function lireSaisie(saisie: SaisieCadre): LectureCadre {
  const largeur = Number(saisie.largeur.replace(",", "."));
  const largeurInvalide =
    saisie.largeur.trim() === "" ||
    !Number.isFinite(largeur) ||
    largeur < LARGEUR_CADRE_MIN_MM ||
    largeur > LARGEUR_CADRE_MAX_MM;
  return {
    reglages: {
      nom: saisie.nom,
      largeur_mm: largeurInvalide ? 0 : largeur,
      fenetre: saisie.fenetre,
      couleurs: saisie.couleurs,
      epaisseur_mm: saisie.epaisseur,
      ecart_mm: saisie.ecart,
      led: saisie.led,
    },
    nomInvalide: !NOM_MOTIF.test(saisie.nom),
    largeurInvalide,
  };
}

/**
 * Le passe-partout : celui de l'apercu (les cotes suivent le profil `cadre.json`), sinon la
 * valeur de depart du profil, le temps du premier apercu.
 */
export function passePartout(apercu: ApercuReponse | null): number {
  if (!apercu) return PASSE_PARTOUT_MM;
  const [plaque] = apercu.dimensions.plaques_mm;
  const [fenetre] = apercu.dimensions.fenetre_mm;
  return (plaque - fenetre) / 2;
}

const FENETRES: readonly Option<Fenetre>[] = [
  { valeur: "rectangle", libelle: "Rectangle" },
  { valeur: "ronde", libelle: "Ronde" },
];

const COULEURS = [
  "auto",
  ...Array.from({ length: COULEURS_CADRE_MAX - COULEURS_CADRE_MIN + 1 }, (_, i) =>
    String(COULEURS_CADRE_MIN + i),
  ),
];

/** 1,2 a 2 mm par pas de 0,2 : arrondis au dixieme, pour ne pas envoyer 0.6000000000000001. */
const EPAISSEURS = Array.from(
  { length: Math.round((EPAISSEUR_MAX_MM - EPAISSEUR_MIN_MM) / EPAISSEUR_PAS_MM) + 1 },
  (_, i) => Math.round((EPAISSEUR_MIN_MM + i * EPAISSEUR_PAS_MM) * 10) / 10,
);

export function CarteReglages({
  saisie,
  changer,
  avecImage,
  ratio,
  apercu,
  className,
}: {
  saisie: SaisieCadre;
  changer: (saisie: SaisieCadre) => void;
  avecImage: boolean;
  /** Hauteur sur largeur de l'image deposee ; `null` avant la premiere. */
  ratio: number | null;
  /** Le dernier apercu de l'image, pour les aides qui citent ses chiffres. */
  apercu: ApercuReponse | null;
  className?: string;
}) {
  const id = useId();
  const lecture = lireSaisie(saisie);
  const poser = (partiel: Partial<SaisieCadre>): void => changer({ ...saisie, ...partiel });
  // Un nom vide avant la premiere image n'est pas une faute : rien ne peut partir.
  const nomFautif = lecture.nomInvalide && (avecImage || saisie.nom !== "");
  const bordure = passePartout(apercu);

  return (
    <section aria-labelledby={`${id}-titre`} className={cn(CARTE, className)}>
      <h2 className="mb-3 text-[15px] font-semibold" id={`${id}-titre`}>
        Réglages
      </h2>

      <Ligne
        aide={aideNom(saisie.nom, !lecture.nomInvalide)}
        aideId={`${id}-nom-aide`}
        erreur={
          nomFautif ? "Minuscules, chiffres et tirets seulement, 40 caractères au plus." : null
        }
        libelle="Nom"
        pour={`${id}-nom`}
      >
        <input
          aria-describedby={`${id}-nom-aide`}
          aria-invalid={nomFautif || undefined}
          autoCapitalize="none"
          autoComplete="off"
          className={cn(CHAMP, "w-full")}
          id={`${id}-nom`}
          maxLength={60}
          onChange={(event) => poser({ nom: event.target.value })}
          placeholder="pris du nom de l'image"
          spellCheck={false}
          type="text"
          value={saisie.nom}
        />
      </Ligne>

      <Ligne
        aide={aideLargeur(lecture.reglages.largeur_mm, ratio, bordure)}
        aideId={`${id}-largeur-aide`}
        erreur={lecture.largeurInvalide ? "De 80 à 230 mm." : null}
        libelle="Largeur"
        pour={`${id}-largeur`}
      >
        <span className="inline-flex items-center gap-2 text-sm">
          <input
            aria-describedby={`${id}-largeur-aide`}
            aria-invalid={lecture.largeurInvalide || undefined}
            className={cn(CHAMP, "w-20")}
            id={`${id}-largeur`}
            inputMode="decimal"
            max={LARGEUR_CADRE_MAX_MM}
            min={LARGEUR_CADRE_MIN_MM}
            onChange={(event) => poser({ largeur: event.target.value })}
            type="number"
            value={saisie.largeur}
          />
          mm
        </span>
      </Ligne>

      <Ligne
        aide={`passe-partout de ${nombreFr(bordure, 1)} mm, de la couleur de la première plaque`}
        aideId={`${id}-fenetre-aide`}
        idLibelle={`${id}-fenetre`}
        libelle="Fenêtre"
      >
        <Bascule
          changer={(fenetre) => poser({ fenetre })}
          decritPar={`${id}-fenetre-aide`}
          idLibelle={`${id}-fenetre`}
          options={FENETRES}
          valeur={saisie.fenetre}
        />
      </Ligne>

      <Ligne
        aide={aideCouleurs(saisie.couleurs, apercu)}
        aideId={`${id}-couleurs-aide`}
        libelle="Couleurs"
        pour={`${id}-couleurs`}
      >
        <select
          aria-describedby={`${id}-couleurs-aide`}
          className={cn(CHAMP, "pr-7")}
          id={`${id}-couleurs`}
          onChange={(event) =>
            poser({
              couleurs: event.target.value === "auto" ? "auto" : Number(event.target.value),
            })
          }
          value={String(saisie.couleurs)}
        >
          {COULEURS.map((nombre) => (
            <option key={nombre} value={nombre}>
              {nombre}
            </option>
          ))}
        </select>
      </Ligne>

      <Ligne
        aide="épaisseur de chaque plaque, de 1,2 à 2 mm"
        aideId={`${id}-epaisseur-aide`}
        libelle="Plaques"
        pour={`${id}-epaisseur`}
      >
        <span className="inline-flex items-center gap-2 text-sm">
          <select
            aria-describedby={`${id}-epaisseur-aide`}
            className={cn(CHAMP, "pr-7")}
            id={`${id}-epaisseur`}
            onChange={(event) => poser({ epaisseur: Number(event.target.value) })}
            value={String(saisie.epaisseur)}
          >
            {EPAISSEURS.map((epaisseur) => (
              <option key={epaisseur} value={String(epaisseur)}>
                {nombreFr(epaisseur, 1)}
              </option>
            ))}
          </select>
          mm
        </span>
      </Ligne>

      <Ligne
        aide="entre deux plaques voisines : plus c'est grand, plus le cadre est profond"
        aideId={`${id}-ecart-aide`}
        libelle="Écart entre les plaques"
        pour={`${id}-ecart`}
      >
        <span className="flex items-center gap-3 text-sm">
          <input
            aria-describedby={`${id}-ecart-aide`}
            aria-valuetext={`${nombreFr(saisie.ecart, 1)} mm`}
            className="min-w-0 flex-1 accent-primary"
            id={`${id}-ecart`}
            max={ECART_MAX_MM}
            min={ECART_MIN_MM}
            onChange={(event) => poser({ ecart: Number(event.target.value) })}
            step={ECART_PAS_MM}
            type="range"
            value={saisie.ecart}
          />
          {/* aria-live="off" : `output` est une zone `status` implicite, et le curseur annonce deja
              sa valeur par `aria-valuetext` : sans cela, un lecteur d'ecran l'annoncerait deux fois. */}
          <output
            aria-live="off"
            className="w-14 shrink-0 text-right tabular-nums"
            htmlFor={`${id}-ecart`}
          >
            {nombreFr(saisie.ecart, 1)} mm
          </output>
        </span>
      </Ligne>

      <Ligne
        aide="Ruban USB 5 V de 8 mm collé autour du logement, derrière le fond, qui s'imprime alors à 0,6 mm. Les couleurs claires du fond s'allument, les foncées à peine."
        aideId={`${id}-led-aide`}
        libelle="Éclairage LED (contre-jour)"
        pour={`${id}-led`}
      >
        <input
          aria-describedby={`${id}-led-aide`}
          checked={saisie.led}
          className={cn(
            "relative h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-muted-foreground transition-colors checked:bg-primary",
            "before:absolute before:top-[3px] before:left-[3px] before:h-3.5 before:w-3.5 before:rounded-full before:bg-card before:transition-transform checked:before:translate-x-4",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
          )}
          id={`${id}-led`}
          onChange={(event) => poser({ led: event.target.checked })}
          type="checkbox"
        />
      </Ligne>
    </section>
  );
}
